import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import Card from "react-bootstrap/Card";
import { Container, Row, Col, Button, Modal, Spinner } from "react-bootstrap";
import { dataContext } from "components/dataContext/dataContext";
import DataTable from "react-data-table-component";
import "../assets/css/dataTable.css";

import { AccountContext } from "components/Account/Account";
import consultaDLQ from "api/consultaDLQ";



function DLQMonitor() {
  const { data } = useContext(dataContext);
  const { getSession } = useContext(AccountContext);

  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [redriving, setRedriving] = useState({});
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState(''); 
  const handleClose = () => {
    setShow(false)
  };
  const fetchQueues = useCallback(async () => {
    setLoading(true);
    try {
      const idToken = await getSession().then((session) => session.idToken.jwtToken);
      // console.log("Fetching DLQ data...");
      const resp = await consultaDLQ.getDLQ(idToken);
      setQueues(resp?.data.queues ?? []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [getSession]);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  const redriveDLQ = useCallback(
    async (row) => {
      try {
        setShow(true);
        setLoadingModal(true);
        const idToken = await getSession().then((session) => session.idToken.jwtToken);
        // Llama a la API para redrive
        await consultaDLQ.redriveDLQ(row.queueName, idToken)
        // Muestra mensaje de éxito
        setMessage(`Redrive iniciado para la cola ${row.queueName}. Por favor, monitorea el estado de las colas para verificar el progreso.`);
        setRedriving((prev) => ({ ...prev, [row.queueName]: true }));
        // refresca conteos
        await fetchQueues();
      } catch (err) {
        console.log(err);
        setMessage(`Error al iniciar redrive para la cola ${row.queueName}.`);
      } finally {
        setLoadingModal(false);
        setRedriving((prev) => ({ ...prev, [row.queueName]: false }));

      }
    },
    [getSession, fetchQueues]
  );

  const stats = useMemo(() => {
    const totalColas = queues.length;
    let colasConMensajes = 0;
    let mensajesVisibles = 0;
    queues.forEach((q) => {
      const visible = q.visible ?? 0;
      if (visible > 0) colasConMensajes += 1;
      mensajesVisibles += visible;
    });
    return { totalColas, colasConMensajes, mensajesVisibles };
  }, [queues]);

  const renderCount = (value) => {
    const n = value ?? 0;
    const cls = n > 0 ? "dlq-count dlq-count--alert" : "dlq-count";
    return <span className={cls}>{n}</span>;
  };

  const columns = [
    {
      name: "Cola",
      selector: (row) => row.queueName.split('_')[0] + " " + row.queueName.split('_')[1],
      sortable: true,
      wrap: true,
      width: '20%',
    },
    {
      name: "Visible",
      selector: (row) => row.visible ?? 0,
      cell: (row) => renderCount(row.visible),
      sortable: true,
      right: true,
      width: '14%',
    },
    {
      name: "No visible",
      selector: (row) => row.notVisible ?? 0,
      cell: (row) => <span className="dlq-count">{row.notVisible ?? 0}</span>,
      sortable: true,
      right: true,
      width: '14%',
    },
    {
      name: "Delayed",
      selector: (row) => row.delayed ?? 0,
      cell: (row) => <span className="dlq-count">{row.delayed ?? 0}</span>,
      sortable: true,
      right: true,
      width: '14%',
    },
    {
      name: "Total",
      selector: (row) => row.total ?? 0,
      cell: (row) => <span className="dlq-count dlq-count--strong">{row.total ?? 0}</span>,
      sortable: true,
      right: true,
      width: '14%',
    },
    {
      name: "Acciones",
      cell: (row) => {
        const hasMessages = (row.visible ?? 0) > 0;
        const isBusy = !!redriving[row.queueName];

        return (
          <Button
            size="sm"
            className="btn-row btn-row--redrive"
            disabled={!hasMessages || isBusy}
            onClick={() => redriveDLQ(row)}
          >
            {isBusy ? "Redriving..." : "Redrive"}
          </Button>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <Container fluid className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Monitor DLQ</h2>
          <p className="page-subtitle">
            Última actualización: {data?.lastcheck || '—'}
          </p>
        </div>
      </div>

      <Row className="stats-row">
        <Col xs={12} sm={6} lg={4}>
          <div className="stat-card stat-card--info">
            <div className="stat-card__icon"><i className="nc-icon nc-bullet-list-67" /></div>
            <div className="stat-card__content">
              <span className="stat-card__label">Total colas</span>
              <span className="stat-card__value">{stats.totalColas}</span>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <div className="stat-card stat-card--warning">
            <div className="stat-card__icon"><i className="nc-icon nc-bell-55" /></div>
            <div className="stat-card__content">
              <span className="stat-card__label">Colas con mensajes</span>
              <span className="stat-card__value">{stats.colasConMensajes}</span>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <div className="stat-card stat-card--danger">
            <div className="stat-card__icon"><i className="nc-icon nc-email-85" /></div>
            <div className="stat-card__content">
              <span className="stat-card__label">Mensajes visibles</span>
              <span className="stat-card__value">{stats.mensajesVisibles}</span>
            </div>
          </div>
        </Col>
      </Row>

      <Card className="data-card">
        <Card.Body>
          <div className="data-card__toolbar">
            <span className="toolbar-meta">
              {queues.length} cola{queues.length === 1 ? '' : 's'} monitoreadas
            </span>
            <Button
              className="btn-export btn-refresh"
              onClick={fetchQueues}
              disabled={loading}
            >
              <i className="nc-icon nc-refresh-69" />
              {loading ? 'Actualizando...' : 'Actualizar'}
            </Button>
          </div>
          <DataTable
            className="-striped -highlight"
            fixedHeader
            columns={columns}
            data={queues}
            progressPending={loading}
            persistTableHead
          />
        </Card.Body>
      </Card>
      {/* Modal redrive */}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>Redrive DLQ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingModal && <Spinner animation="border" role="status" className='sticky-top'></Spinner>}
          <p>{!loadingModal && <> {message} </>}</p>
        </Modal.Body>
        <Modal.Footer>
          {!loadingModal && <Button variant="secondary" onClick={handleClose}>
            Cerrar
          </Button>}

        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default DLQMonitor;
